import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/contexts/AuthContext";
import { AuthForm } from "@/components/AuthForm";

type Mode = "sign-in" | "sign-up" | "forgot-password";

interface AuthModalProps {
  isOpen: boolean;
  initialMode?: Mode;
  onClose: () => void;
  /** Replaces the default "Welcome back" / "Create your account" heading. */
  customHeading?: string;
  /** Optional subtext shown below the heading (e.g. value-oriented messaging). */
  customBody?: string;
}

export function AuthModal({ isOpen, initialMode = "sign-in", onClose, customHeading, customBody }: AuthModalProps) {
  const { firebaseConfigured } = useAuth();
  const [mode, setMode] = useState<Mode>(initialMode);

  // Sync mode whenever the modal opens or the requested mode changes so that
  // repeated open/close cycles always start in the correct state.
  useEffect(() => {
    if (isOpen) setMode(initialMode);
  }, [isOpen, initialMode]);

  if (!isOpen) return null;

  const title = customHeading ?? (mode === "sign-in" ? "Welcome back" : "Create your account");

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 px-4 py-6 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="my-auto max-h-[calc(100vh-3rem)] w-full max-w-sm overflow-y-auto rounded-3xl border border-stone-200 bg-white p-8 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-stone-900">{title}</h2>
            <button
              onClick={onClose}
              className="rounded-full p-1.5 text-stone-400 transition hover:bg-stone-100 hover:text-stone-600"
              aria-label="Close"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          {customBody && (
            <p className="mt-2 text-sm leading-relaxed text-stone-500">{customBody}</p>
          )}
        </div>

        {!firebaseConfigured ? (
          <div className="text-center py-4">
            <p className="font-serif text-xl text-stone-900 mb-3">Setup required</p>
            <p className="text-sm text-stone-500 leading-relaxed">
              Firebase authentication is not yet configured. Add the{" "}
              <code className="rounded bg-stone-100 px-1 py-0.5 text-xs">VITE_FIREBASE_*</code>{" "}
              environment variables and restart the app.
            </p>
          </div>
        ) : (
          <AuthForm
            initialMode={mode}
            onModeChange={setMode}
          />
        )}
      </div>
    </div>,
    document.body,
  );
}
